import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  getStorage,
  ref as firebaseStorageRef,
  uploadBytesResumable,
  getDownloadURL,
  updateMetadata,
} from 'firebase/storage';
import styles from './AudioUploadForm.module.scss';

const AUDIO_EXTENSIONS = /\.(mp3|wav|ogg|m4a|flac|aac|aiff|aif|webm)$/i;

const isAudioFile = (file) =>
  file.type.startsWith('audio/') || AUDIO_EXTENSIONS.test(file.name);

const AudioUploadForm = forwardRef((props, formRef) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [dropError, setDropError] = useState('');
  const fileInputRef = useRef(null);

  const selectFile = useCallback((file) => {
    if (!file) return;
    if (!isAudioFile(file)) {
      setDropError('Please drop an audio file (mp3, wav, m4a, etc.)');
      return;
    }
    setDropError('');
    setSelectedFile(file);
    setFileName((prev) => prev || file.name);
  }, []);

  useImperativeHandle(
    formRef,
    () => ({
      selectFile,
      canAcceptDrop: () => uploadStatus !== 'uploading',
    }),
    [selectFile, uploadStatus]
  );

  const handleFileChange = (event) => {
    selectFile(event.target.files[0]);
    event.target.value = '';
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setDropError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileNameChange = (event) => {
    setFileName(event.target.value);
  };

  const handleFileUpload = () => {
    if (selectedFile && fileName) {
      const storage = getStorage();
      const storageRef = firebaseStorageRef(storage, fileName);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);

      setUploadStatus('uploading');

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          setUploadStatus('error');
          console.error('Error uploading file:', error);
        },
        async () => {
          try {
            const uploadDate = new Date().toISOString();
            await updateMetadata(storageRef, { customMetadata: { uploadDate } });
            await getDownloadURL(uploadTask.snapshot.ref);
            setSelectedFile(null);
            setFileName('');
            setUploadStatus('success');
            setUploadProgress(0);
          } catch (error) {
            console.error('Error updating metadata or getting download URL:', error);
            setUploadStatus('error');
          }
        }
      );
    }
  };

  const handleDismissMessage = () => {
    setUploadStatus('idle');
    setUploadProgress(0);
  };

  const isUploading = uploadStatus === 'uploading';

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h1 className={styles.title}>Upload audio</h1>
        <p className={styles.description}>
          Add a new track. You can also drop a file anywhere on this page.
        </p>
      </div>

      <div className={styles.content}>
        <div className={styles.field}>
          <span className={styles.label} id='audio-file-label'>
            Audio file
          </span>
          {selectedFile ? (
            <div className={styles.selectedFile}>
              <span className={styles.fileName}>{selectedFile.name}</span>
              <button
                type='button'
                className={styles.iconButton}
                onClick={handleClearFile}
                disabled={isUploading}
                aria-label='Remove file'
              >
                ×
              </button>
            </div>
          ) : (
            <button
              type='button'
              className={styles.dropzone}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              aria-labelledby='audio-file-label'
            >
              <span className={styles.dropzoneTitle}>Choose a file</span>
              <span className={styles.dropzoneHint}>MP3, WAV, M4A, FLAC, OGG, AIFF</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type='file'
            accept='audio/*'
            onChange={handleFileChange}
            className={styles.hiddenInput}
            tabIndex={-1}
          />
          {dropError && <p className={styles.fieldError}>{dropError}</p>}
        </div>

        <div className={styles.field}>
          <label htmlFor='track-name' className={styles.label}>
            Track name
          </label>
          <input
            id='track-name'
            className={styles.input}
            type='text'
            value={fileName}
            onChange={handleFileNameChange}
            placeholder='Enter a track name'
            disabled={isUploading}
          />
        </div>

        {isUploading && (
          <div className={styles.progress}>
            <div className={styles.progressRow}>
              <span>Uploading</span>
              <span className={styles.progressValue}>{Math.round(uploadProgress)}%</span>
            </div>
            <div
              className={styles.progressBar}
              role='progressbar'
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(uploadProgress)}
            >
              <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {uploadStatus === 'success' && (
          <div className={`${styles.alert} ${styles.success}`} role='status'>
            <span>Upload successful.</span>
            <button type='button' className={styles.alertButton} onClick={handleDismissMessage}>
              Dismiss
            </button>
          </div>
        )}

        {uploadStatus === 'error' && (
          <div className={`${styles.alert} ${styles.error}`} role='alert'>
            <span>Upload failed. Please try again.</span>
            <button type='button' className={styles.alertButton} onClick={handleDismissMessage}>
              Dismiss
            </button>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button
          type='button'
          className={styles.uploadButton}
          onClick={handleFileUpload}
          disabled={!selectedFile || !fileName || isUploading}
        >
          {isUploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </div>
  );
});

AudioUploadForm.displayName = 'AudioUploadForm';

export default AudioUploadForm;
